import { Button } from '@/components/Button/Button';
import { TabScreenContainer } from '@/components/TabScreenContainer';
import { useIsWeb } from '@/hooks/useIsWeb';
import { EmptyStateView } from '@components/EmptyStateView/EmptyStateView';
import { LoadingView } from '@components/LoadingView/LoadingView';
import {
  buildEmptyReportFilters,
  hasActiveReportFilters,
} from '@components/ReportFilters/reportFilters.utils';
import ReportList from '@components/ReportList/ReportList';
import { ScreenHeader } from '@components/ScreenHeader';
import { Ionicons } from '@expo/vector-icons';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { useReportFiltersPanel } from '@hooks/useReportFiltersPanel';
import { useReportsCsvExport } from '@hooks/useReportsCsvExport/useReportsCsvExport.hook';
import { useReportSelection } from '@hooks/useReportSelection';
import { useReportsList } from '@hooks/useReportsList';
import { useMemo, useState } from 'react';
import { useTheme, XStack, YStack } from 'tamagui';
import { REPORTS_PAGE_LIMIT } from './reportsScreen.constants';

export default function ReportsScreen(): React.ReactNode {
  const { t } = useAppTranslation();
  const { isSmallScreen } = useBreakpoint();
  const theme = useTheme();
  const { selectedReportIds, selectedReports, handleSelectionChange } =
    useReportSelection();
  const [pageLimit, setPageLimit] = useState(REPORTS_PAGE_LIMIT);
  const { appliedFilters, filterButton, filtersPanel } = useReportFiltersPanel({
    isSmallScreen,
  });
  const {
    page,
    setPage,
    reportPages,
    reportCount,
    sorting,
    hasFetchedReports,
    hasMore,
    isPending,
    isLoadingMore,
    loadMoreReports,
    handleSortingChange,
  } = useReportsList({ myReports: true, filters: appliedFilters, pageLimit });
  const emptyFilters = useMemo(() => buildEmptyReportFilters(), []);
  const hasFiltersApplied = hasActiveReportFilters(
    appliedFilters,
    emptyFilters,
  );
  const exportIconColor = theme.color?.val;
  const { exportCsv, canExport } = useReportsCsvExport({
    reports: selectedReports,
    filePrefix: 'reports',
  });
  const isWeb = useIsWeb();

  const exportButton =
    isWeb && !isSmallScreen ? (
      <Button
        variant="outlined"
        title={t('reports.exportCsv')}
        onPress={exportCsv}
        icon={
          <Ionicons name="download-outline" size={18} color={exportIconColor} />
        }
        disabled={!canExport}
        accessibilityLabel={t('reports.exportCsv')}
      />
    ) : null;
  const headerActions = exportButton ? (
    <XStack gap="$2" alignItems="center">
      {exportButton}
      {filterButton}
    </XStack>
  ) : (
    filterButton
  );

  if (isPending && !hasFetchedReports) {
    return <LoadingView />;
  }

  if (reportCount === 0 && !hasFiltersApplied) {
    return (
      <TabScreenContainer>
        <ScreenHeader
          title={t('navigation.myReports')}
          actions={headerActions}
        />
        <EmptyStateView
          title={t('reports.emptyStateTitle')}
          description={t('reports.emptyStateDescription')}
        />
      </TabScreenContainer>
    );
  }

  return (
    <TabScreenContainer>
      <ScreenHeader title={t('navigation.myReports')} actions={headerActions} />
      <XStack flex={1} gap="$4">
        <YStack flex={1}>
          <ReportList
            reportPages={reportPages}
            reportCount={reportCount}
            sortedColumns={sorting}
            setPage={(page) => setPage(page)}
            onSortingChange={handleSortingChange}
            page={page}
            selectedReportIds={selectedReportIds}
            onSelectionChange={handleSelectionChange}
            pageSize={pageLimit}
            onPageSizeChange={setPageLimit}
            onLoadMore={loadMoreReports}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
          />
        </YStack>
        {!isSmallScreen && filtersPanel}
      </XStack>
      {isSmallScreen && filtersPanel}
    </TabScreenContainer>
  );
}
